import { supabase } from '../../infrastructure/supabase/supabaseClient';
import { LocalSearchEngine } from '../search/LocalSearchEngine';
import { type SearchIndexItem, type SearchFilterType, SearchIndexItemSchema } from '../../domain/types/search.types';

export class DirectoryService {
  private static engine: LocalSearchEngine | null = null;
  private static isIndexing: boolean = false;

  /**
   * Invalida la caché del buscador local forzando una re-descarga 
   * en la próxima búsqueda.
   */
  static clearEngine(): void {
    this.engine = null;
    console.info('[DirectoryService] Caché invalidada. Se recargará en la próxima búsqueda.');
  }

  /**
   * Inicializa el motor de búsqueda local descargando la vista plana de Supabase.
   */
  static async initializeEngine(): Promise<void> {
    if (this.engine || this.isIndexing) return;
    this.isIndexing = true;

    try {
      // Descargamos la vista plana completa (idealmente cacheada en CDN)
      const { data, error } = await supabase
        .from('vw_directorio_index')
        .select('*');

      if (error) throw error;

      if (data) {
        const items: SearchIndexItem[] = data
          .map((row: unknown) => {
            // Fix #2: tipar y validar con Zod — datos malformados de la BD no pasan al índice
            const raw = row as Record<string, unknown>;
            const parsed = SearchIndexItemSchema.safeParse({
              id:                  raw.id_global,
              nombre:              raw.nombre_principal,
              titulo_principal:    raw.titulo_principal,
              especialidad:        raw.subtitulo ?? '',
              licencia_profesional: raw.licencia_profesional,
              lat:                 raw.latitud   ?? 0,
              lng:                 raw.longitud  ?? 0,
              reputacion_score:    100,
              badge_recomendado:   false,
              verificado:          raw.es_verificado,
              agenda_activa:       raw.agenda_activa ?? false,
              // Fix #3: sin disponibilidad falsa — la real vendrá del módulo de agenda
              disponibilidad_status: 'ninguna',
              search_blob:         raw.tags_busqueda,
              imagen_url:          raw.imagen_url,
              ciudad_id:           raw.ciudad_id,
            });

            if (!parsed.success) {
              console.warn('[DirectoryService] Fila inválida descartada:', parsed.error.issues[0]?.message, raw.id_global);
              return null;
            }
            return parsed.data;
          })
          .filter((item): item is SearchIndexItem => item !== null);

        this.engine = new LocalSearchEngine();
        await this.engine.indexItems(items);
        console.info('[DirectoryService] LocalSearchEngine inicializado con', items.length, 'profesionales.');
      }
    } catch (error) {
      console.error('[DirectoryService] Error al inicializar:', error);
    } finally {
      this.isIndexing = false;
    }
  }

  /**
   * Busca profesionales utilizando FlexSearch y H3 localmente.
   */
  static async getProfessionals(
    query: string = '', 
    userLat: number | null = null, 
    userLng: number | null = null,
    filter: SearchFilterType = 'recomendado',
    targetCityId: number | null = null,
    targetModalities: string[] = []
  ): Promise<SearchIndexItem[]> {
    if (!this.engine) {
      await this.initializeEngine();
    }

    // Si aún así falló o está vacío
    if (!this.engine) return [];

    // Si no enviaron coordenadas (ej. permiso negado), pasamos 0,0 para que el score de distancia sea 0
    const lat = userLat ?? 0;
    const lng = userLng ?? 0;

    return await this.engine.search(query, lat, lng, filter, targetCityId, targetModalities);
  }
}
